import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/app/hooks';
import { clearCurrentWebinar } from '@/features/webinar/webinarSlice';
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import AiWizardSection from '@/pages/create-webinar/AiWizardSection';

type CreateWebinarModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function CreateWebinarModal({ open, onOpenChange }: CreateWebinarModalProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('webinars');
  const [aiWizardOpen, setAiWizardOpen] = useState(false);

  const handleCreateFromScratch = () => {
    onOpenChange(false);
    dispatch(clearCurrentWebinar());
    navigate('/create-webinar/configuration');
  };

  const handleCreateWithAi = () => {
    onOpenChange(false);
    setAiWizardOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-205">
          <DialogHeader>
            <DialogTitle className="text-3xl">{t('createAndList.modal.title')}</DialogTitle>
            <p className="text-secondary">{t('createAndList.modal.description')}</p>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="h-auto sm:min-h-80 gap-12 rounded-[20px] overflow-hidden bg-dark text-white border-2 border-button-primary flex flex-col sm:flex-row">
              <img
                src="/assets/create-webinar-ai.jpg"
                alt=""
                className="w-full h-40 sm:h-full sm:flex-1 sm:min-w-0 object-cover sm:rounded-tr-[120px] drop-shadow-[0_0_54.5px_rgba(46,255,184,0.50)]"
              />
              <div className="p-6 flex flex-col gap-4 flex-1">
                <p className="text-2xl font-semibold">
                  {t('createAndList.modal.aiTitlePrefix')}{' '}
                  <span className="text-button-primary">
                    {t('createAndList.modal.aiTitleHighlight')}
                  </span>{' '}
                  {t('createAndList.modal.aiTitleSuffix')}
                </p>
                <p className="text-sm text-white/70">{t('createAndList.modal.aiDescription')}</p>
                <Button onClick={handleCreateWithAi} className="w-full mt-auto">
                  <Sparkles className="size-4" />
                  {t('createAndList.modal.aiButton')}
                </Button>
              </div>
            </div>

            <div className="h-auto sm:min-h-80 gap-12 rounded-[20px] overflow-hidden bg-decorative-2 flex flex-col sm:flex-row">
              <img
                src="/assets/create-webinar-scratch.jpg"
                alt=""
                className="w-full h-40 sm:h-full sm:flex-1 sm:min-w-0 object-cover sm:rounded-tr-[120px]"
              />
              <div className="p-6 flex flex-col gap-4 flex-1">
                <p className="text-2xl font-semibold text-black">
                  {t('createAndList.modal.scratchTitle')}
                </p>
                <p className="text-sm text-secondary">
                  {t('createAndList.modal.scratchDescription')}
                </p>
                <Button
                  variant="outline"
                  className="w-full mt-auto"
                  onClick={handleCreateFromScratch}
                >
                  {t('createAndList.modal.scratchButton')}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {aiWizardOpen && <AiWizardSection onClose={() => setAiWizardOpen(false)} />}
    </>
  );
}
